import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject, input } from "@angular/core"
import { AgTextDirective } from "@shared/directives/ag-text.directive"
import { IconImageService } from "@shared/services/icon-image.service"
import { FlowHelperTextComponent } from "../flow-helper-text/flow-helper-text.component"
import { CheckboxType, DropDownDivider, DropDownFooter, DropDownItem, DropDownType, FlowDropDownComponent } from "../flow-drop-down/flow-drop-down.component"

@Component({
  selector: "flow-select",
  templateUrl: "./flow-select.component.html",
  styleUrl: "./flow-select.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlowSelectComponent {
  public readonly iconService = inject(IconImageService)

  // ── Forwarded to flow-drop-down ──────────────────────────────
  @Input() public type: DropDownType = "item"
  /** Also drives the button's label via the selected items. */
  @Input() public items: DropDownItem[] = []
  @Input() public dividers: DropDownDivider[] = []
  @Input() public footer: DropDownFooter | null = null
  /** Shows the drop-down's search bar. Empty string hides it. */
  @Input() public searchbarLabel: string = ""
  /** Lets the user append custom items via a footer trigger and an inline add row. */
  @Input() public addable: boolean = false
  @Input() public addPlaceholder: string = ""
  /** Label for the footer trigger that reveals the add row. */
  @Input() public addTriggerLabel: string = "Add item"
  /** Checkbox type for added items. Defaults to matching the existing items. */
  @Input() public addedCheckbox: CheckboxType | null = null

  @Input() public emptyTitle: string = "Nothing here yet"
  @Input() public emptySubtitle: string = ""
  @Input() public noResultsTitle: string = "No search results"
  @Input() public noResultsSubtitle: string = "We couldn't find what you're looking for"
  /** Max height of the drop-down panel before it scrolls. */
  @Input() public height: number = 360
  /** Opens the panel above the button instead of below. */
  @Input() public foldUp: boolean = false
  /** Aligns the panel's right edge with the button's. */
  @Input() public alignRight: boolean = false

  /** Defaults to `width` — only set this to size the panel differently to the button. */
  @Input()
  public get dropdownWidth(): number {
    return this._dropdownWidth ?? this.width
  }
  public set dropdownWidth(value: number) {
    this._dropdownWidth = value
  }
  private _dropdownWidth: number | null = null

  // ── Select only ──────────────────────────────────────────────
  @Input() public width: number = 250
  @Input() public placeholder: string = "Select"
  /** Optional label above the button. */
  @Input() public title: string = ""
  /** Optional helper text below the button. */
  @Input() public helperText: string = ""
  @Input() public helperIcon: boolean = true
  /** Red border and destructive helper text. */
  @Input() public error: boolean = false
  /** Which side of the button the chevron sits on. */
  @Input() public iconPosition: "leading" | "trailing" = "trailing"
  @Input() public disabled: boolean = false
  /** Shows "N selected" on the button instead of listing the selected titles. */
  @Input() public showSelectionCount: boolean = false

  @Output() public selectionChange = new EventEmitter<DropDownItem[]>()
  @Output() public itemsChange = new EventEmitter<DropDownItem[]>()
  @Output() public itemAdded = new EventEmitter<DropDownItem>()
  @Output() public itemRemoved = new EventEmitter<DropDownItem>()
  @Output() public footerPrimaryAction = new EventEmitter<boolean>()
  @Output() public footerSecondaryAction = new EventEmitter<boolean>()
  @Output() public footerTertiaryAction = new EventEmitter<boolean>()

  public open: boolean = false

  public get hasSelection(): boolean {
    return this.items.some((item) => item.selected)
  }

  public get displayLabel(): string {
    const selected = this.items.filter((item) => item.selected)
    if (!selected.length) return this.placeholder
    if (this.showSelectionCount) return `${selected.length} selected`
    return selected.map((item) => item.title).join(", ")
  }

  // checkbox ticks mutate items in place, so the committed state has to be kept
  // aside while the panel is open for the footer's Cancel to restore. keyed by item
  // rather than by index, so adding or deleting a custom item can't misalign it
  private pendingSnapshot: Map<DropDownItem, boolean> | null = null

  public toggle(): void {
    if (this.disabled) return
    if (!this.open && this.footer) this.takeSnapshot()
    this.open = !this.open
  }

  private takeSnapshot(): void {
    this.pendingSnapshot = new Map(this.items.map((item) => [item, item.selected]))
  }

  private restoreSnapshot(): void {
    if (!this.pendingSnapshot) return
    this.items.forEach((item) => (item.selected = this.pendingSnapshot.get(item) ?? false))
    this.pendingSnapshot = null
  }

  public onItemSelected(index: number): void {
    const item = this.items[index]
    if (!item) return

    if (item.checkbox === "checkbox") return

    for (const other of this.items) other.selected = other === item
    this.selectionChange.emit(this.items.filter((i) => i.selected))
    if (!this.footer) this.open = false
  }

  public onItemsChange(items: DropDownItem[]): void {
    this.items = items
    this.itemsChange.emit(items)
  }

  // a newly added item has no snapshot entry, so Cancel would untick it —
  // record it as unselected instead and let the user's own tick stand
  public onItemAdded(item: DropDownItem): void {
    this.pendingSnapshot?.set(item, false)
    this.itemAdded.emit(item)
  }

  public onItemRemoved(item: DropDownItem): void {
    this.pendingSnapshot?.delete(item)
    this.itemRemoved.emit(item)
  }

  public onFooter(action: "primary" | "secondary" | "tertiary"): void {
    if (action === "primary") {
      this.pendingSnapshot = null
      this.selectionChange.emit(this.items.filter((i) => i.selected))
      this.footerPrimaryAction.emit(true)
    } else if (action === "secondary") {
      this.restoreSnapshot()
      this.footerSecondaryAction.emit(true)
    } else {
      this.items.forEach((item) => (item.selected = false))
      this.footerTertiaryAction.emit(true)
    }
    this.open = false
  }

  /** Dismissing without confirming discards pending ticks, same as Cancel. */
  public onOpenChange(open: boolean): void {
    if (!open) this.restoreSnapshot()
    this.open = open
  }
}
